import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import {
    App,
    Button,
    Card,
    Col,
    Form,
    Image,
    Input,
    Modal,
    Popconfirm,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    Upload,
} from 'antd'
import type { TableColumnsType } from 'antd'
import {
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    EnvironmentOutlined,
    EyeOutlined,
    PictureOutlined,
    ReloadOutlined,
    SearchOutlined,
    UploadOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { PageLayout } from '../components/layout'
import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../components/common/FilterBar'
import {
    PaginatedListCard,
    PaginatedListContainer,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../components/common/PaginatedListTable'
import MapSelector from '../components/common/MapSelector'
import { RegionSelect } from '../components/common/RegionSelect'
import { useDedupedMessage } from '../hooks/useDedupedMessage'
import { usePermissions } from '../hooks/usePermissions'
import { localeService } from '../services/localeService'
import { getRegionPath } from '../services/regionService'
import {
    deleteDevice,
    exportDevices,
    getDevice,
    getDeviceFilterOptions,
    queryDevices,
    updateDevice,
    uploadDeviceInstallationImage,
} from '../services/deviceService'
import { lookupProjects } from '../services/projectService'
import {
    invalidateManagedObjectUrlCache,
    resolveManagedObjectUrls,
} from '../services/ossService'
import {
    DEVICE_PURCHASE_VERSION_UNSET_FILTER,
    type Device,
    type DeviceExportProjectOption,
    type DeviceQueryParams,
    type DeviceUpdate,
} from '../types/device'
import {
    filterSearchOptionByLabel,
    normalizeSearchText,
} from '../utils/searchText'
import type { ProjectLookupItem } from '../types/project'
import type { VersionOption } from '../types/version'
import {
    type DeviceLureFilter,
    type DeviceStatusFilter,
} from './deviceManagement.utils'
import {
    buildRegionPathIds,
    getUncachedRegionIds,
    type RegionPathIdsByRegionId,
} from '../utils/regionFilter'
import { filterRegionSelectOption } from '../utils/regionSearch'
import {
    buildAllFilterOptions,
    getAllAwareDisplayValues,
    normalizeAllAwareValues,
    type FilterSelectValue,
} from '../components/statistics/filterSelectUtils'
import {
    extractApiErrorDetail,
    getLocalizedApiErrorMessage,
} from '../utils/apiErrorI18n'
import {
    getDevicePackageDisplayName,
    getDevicePackageOptionDisplayName,
} from '../utils/devicePackageDisplay'
import { formatPackageBusinessDate } from '../utils/packageBusinessDate'
import './DeviceManagement.css'

const buildRegionLabel = (
    path: Array<{
        name: string
        is_active?: boolean
    }>
): string => {
    const label = path.map((region) => region.name).join(' / ')
    const currentRegion = path[path.length - 1]
    return currentRegion?.is_active === false ? `${label}（已停用）` : label
}

const buildRegionOptionLabels = (
    path: Array<{
        id: number
        name: string
        is_active?: boolean
    }>
): Record<number, string> =>
    path.reduce<Record<number, string>>((acc, region, index) => {
        const label =
            path
                .slice(0, index + 1)
                .map((item) => item.name)
                .join(' / ') || String(region.id)
        acc[region.id] =
            region.is_active === false ? `${label}（已停用）` : label
        return acc
    }, {})

const createDefaultManagementFilters = () => ({
    searchName: '',
    selectedProjectIds: [] as number[],
    selectedRegionIds: [] as number[],
    statusFilter: 'all' as DeviceStatusFilter,
    lureFilter: 'all' as DeviceLureFilter,
    purchaseVersionFilter: '',
    cloudServiceStatusFilter: 'all' as
        | NonNullable<Device['cloud_service_status']>
        | 'all',
})

type DeviceManagementFilters = ReturnType<typeof createDefaultManagementFilters>

const DEVICE_STATUS_FILTERS: DeviceStatusFilter[] = ['all', 'online', 'offline']
const DEVICE_LURE_FILTERS: DeviceLureFilter[] = [
    'all',
    'expiring_1_days',
    'expiring_3_days',
    'expiring_7_days',
    'expiring_15_days',
    'expired',
]
const DEFAULT_PROJECT_DISPLAY_NAME = 'Default Project'
const REGION_PATH_REQUEST_CONCURRENCY = 4

const mapWithConcurrency = async <T, R>(
    items: T[],
    concurrency: number,
    mapper: (item: T) => Promise<R>
): Promise<R[]> => {
    const results = new Array<R>(items.length)
    let nextIndex = 0

    const workers = Array.from(
        { length: Math.min(concurrency, items.length) },
        async () => {
            while (nextIndex < items.length) {
                const currentIndex = nextIndex
                nextIndex += 1
                results[currentIndex] = await mapper(items[currentIndex])
            }
        }
    )

    await Promise.all(workers)
    return results
}

const normalizeRouteStatusFilter = (
    value: string | null
): DeviceStatusFilter =>
    DEVICE_STATUS_FILTERS.includes(value as DeviceStatusFilter)
        ? (value as DeviceStatusFilter)
        : 'all'

const normalizeRouteLureFilter = (value: string | null): DeviceLureFilter =>
    DEVICE_LURE_FILTERS.includes(value as DeviceLureFilter)
        ? (value as DeviceLureFilter)
        : 'all'

const parseRouteProjectIds = (value: string | null): number[] =>
    Array.from(
        new Set(
            (value || '')
                .split(',')
                .map((item) => Number(item.trim()))
                .filter((item) => Number.isInteger(item) && item > 0)
        )
    )

const getDeviceProjectDisplayName = (device: Device): string =>
    device.project_id == null ? '' : device.project_name || ''

const getDeviceRootProjectDisplayName = (device: Device): string =>
    device.project_id == null ? '' : device.root_project_name || ''

const isDeviceStaleError = (error: unknown): boolean => {
    if (!isAxiosError(error)) {
        return false
    }

    if (error.response?.status === 404) {
        return true
    }

    const detail = extractApiErrorDetail(error)
    return detail === '设备不存在' || detail === 'Device not found'
}

const DEVICE_NAME_MAX_LENGTH = 50
const DEVICE_REMARK_MAX_LENGTH = 255
const DeviceManagement: React.FC = () => {
    const { message: rawMessage } = App.useApp()
    const message = useDedupedMessage(rawMessage)
    const { t, i18n } = useTranslation(['device', 'navigation'])
    const {
        canDeleteDevices,
        canExportDevices,
        canReadDeviceDetails,
        canUpdateDevices,
        canUpdateDeviceLocation,
    } = usePermissions()
    const [searchParams] = useSearchParams()
    const routeStatusFilter = normalizeRouteStatusFilter(
        searchParams.get('status')
    )
    const routeLureFilter = normalizeRouteLureFilter(
        searchParams.get('lure_filter')
    )
    const routeProjectIdsParam = searchParams.get('project_ids')
    const routeProjectIds = useMemo(
        () => parseRouteProjectIds(routeProjectIdsParam),
        [routeProjectIdsParam]
    )
    const createRouteDefaultFilters = (): DeviceManagementFilters => ({
        ...createDefaultManagementFilters(),
        selectedProjectIds: routeProjectIds,
        statusFilter: routeStatusFilter,
        lureFilter: routeLureFilter,
    })

    const [devices, setDevices] = useState<Device[]>([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [environmentRefreshing, setEnvironmentRefreshing] = useState(false)
    const environmentRefreshRequestIdRef = useRef(0)
    const [editModalVisible, setEditModalVisible] = useState(false)
    const [mapModalVisible, setMapModalVisible] = useState(false)
    const [exportModalVisible, setExportModalVisible] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [currentDevice, setCurrentDevice] = useState<Device | null>(null)
    const [searchName, setSearchName] = useState(
        () => createRouteDefaultFilters().searchName
    )
    const [selectedRegionIds, setSelectedRegionIds] = useState<number[]>(
        () => createRouteDefaultFilters().selectedRegionIds
    )
    const [statusFilter, setStatusFilter] = useState<DeviceStatusFilter>(
        () => createRouteDefaultFilters().statusFilter
    )
    const [lureFilter, setLureFilter] = useState<DeviceLureFilter>(
        () => createRouteDefaultFilters().lureFilter
    )
    const [purchaseVersionFilter, setPurchaseVersionFilter] = useState(
        () => createRouteDefaultFilters().purchaseVersionFilter
    )
    const [cloudServiceStatusFilter, setCloudServiceStatusFilter] = useState(
        () => createRouteDefaultFilters().cloudServiceStatusFilter
    )
    const [appliedFilters, setAppliedFilters] =
        useState<DeviceManagementFilters>(() => createRouteDefaultFilters())
    const [exportProjectIds, setExportProjectIds] = useState<number[]>([])
    const [exportRegionIds, setExportRegionIds] = useState<number[]>([])
    const [exportStatusFilter, setExportStatusFilter] =
        useState<DeviceStatusFilter>('all')
    const [exportLureFilter, setExportLureFilter] =
        useState<DeviceLureFilter>('all')
    const [exportPurchaseVersionFilter, setExportPurchaseVersionFilter] =
        useState('')
    const [exportCloudServiceStatusFilter, setExportCloudServiceStatusFilter] =
        useState<NonNullable<Device['cloud_service_status']> | 'all'>('all')
    const [viewModalVisible, setViewModalVisible] = useState(false)
    const [openDeleteDeviceId, setOpenDeleteDeviceId] = useState<string | null>(
        null
    )
    const [editImagePreviewUrl, setEditImagePreviewUrl] = useState<
        string | null
    >(null)
    const [editImageUploading, setEditImageUploading] = useState(false)
    const [projectOptions, setProjectOptions] = useState<ProjectLookupItem[]>(
        []
    )
    const [exportProjectOptions, setExportProjectOptions] = useState<
        DeviceExportProjectOption[]
    >([])
    const [imageUrlMap, setImageUrlMap] = useState<Map<string, string>>(
        new Map()
    )
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [queryTrigger, setQueryTrigger] = useState(0)
    const [sortBy, setSortBy] = useState<
        | 'status_changed_at'
        | 'last_active_at'
        | 'lure_remind_at'
        | 'created_at'
        | undefined
    >(undefined)
    const [sortOrder, setSortOrder] = useState<
        'ascend' | 'descend' | undefined
    >(undefined)
    const [nameOptions, setNameOptions] = useState<string[]>([])
    const [purchaseVersionOptions, setPurchaseVersionOptions] = useState<
        VersionOption[]
    >([])
    const [exportPreviewCount, setExportPreviewCount] = useState(0)
    const [regionOptionLabels, setRegionOptionLabels] = useState<
        Record<number, string>
    >({})
    const regionOptionLabelCacheRef = useRef<Record<number, string>>({})
    const regionPathIdsCacheRef = useRef<RegionPathIdsByRegionId>({})
    const editImageObjectUrlRef = useRef<string | null>(null)
    const editImageUploadRequestIdRef = useRef(0)
    const activeEditDeviceIdRef = useRef<string | null>(null)
    const latestDeviceRequestIdRef = useRef(0)
    const [form] = Form.useForm()
    const clearEditImageObjectUrl = useCallback(() => {
        if (editImageObjectUrlRef.current) {
            URL.revokeObjectURL(editImageObjectUrlRef.current)
            editImageObjectUrlRef.current = null
        }
    }, [])

    const closeEditModal = useCallback(() => {
        editImageUploadRequestIdRef.current += 1
        activeEditDeviceIdRef.current = null
        setEditImageUploading(false)
        setEditModalVisible(false)
        setCurrentDevice(null)
        setEditImagePreviewUrl(null)
        clearEditImageObjectUrl()
        form.resetFields()
    }, [clearEditImageObjectUrl, form])

    useEffect(() => () => clearEditImageObjectUrl(), [clearEditImageObjectUrl])

    const loadFilterOptions = useCallback(async () => {
        const data = await getDeviceFilterOptions()
        setNameOptions(
            Array.from(
                new Set([
                    ...(data.names || []),
                    ...(data.project_names || []).filter(
                        (projectName) =>
                            projectName !== DEFAULT_PROJECT_DISPLAY_NAME
                    ),
                ])
            ).sort()
        )
        setPurchaseVersionOptions(
            data.purchase_version_options?.length
                ? data.purchase_version_options
                : (data.purchase_versions || []).map((version) => ({
                      key: null,
                      name: version,
                      value: version,
                  }))
        )
        setExportProjectOptions(data.export_project_options || [])

        const regionIds = getUncachedRegionIds(
            data.region_ids,
            regionPathIdsCacheRef.current
        )

        if (regionIds.length === 0) {
            return
        }

        const entries = await mapWithConcurrency(
            regionIds,
            REGION_PATH_REQUEST_CONCURRENCY,
            async (regionId) => {
                try {
                    const path = await getRegionPath(regionId)
                    return [
                        regionId,
                        buildRegionLabel(path) || String(regionId),
                        buildRegionPathIds(path),
                        buildRegionOptionLabels(path),
                    ] as const
                } catch {
                    const fallbackPathIds = [regionId]
                    return [
                        regionId,
                        String(regionId),
                        fallbackPathIds,
                        { [regionId]: String(regionId) },
                    ] as const
                }
            }
        )

        const nextRegionPaths = entries.reduce<RegionPathIdsByRegionId>(
            (acc, entry) => {
                const [regionId, , pathIds] = entry
                acc[regionId] = pathIds
                return acc
            },
            {}
        )
        const nextRegionOptionLabels = entries.reduce<Record<number, string>>(
            (acc, entry) => ({
                ...acc,
                ...entry[3],
            }),
            {}
        )

        regionOptionLabelCacheRef.current = {
            ...regionOptionLabelCacheRef.current,
            ...nextRegionOptionLabels,
        }
        regionPathIdsCacheRef.current = {
            ...regionPathIdsCacheRef.current,
            ...nextRegionPaths,
        }
        setRegionOptionLabels((prev) => ({
            ...prev,
            ...nextRegionOptionLabels,
        }))
    }, [])

    const deviceQueryFilters = useMemo<DeviceQueryParams>(
        () => ({
            page_size: pageSize,
            search_name: appliedFilters.searchName.trim() || undefined,
            project_ids: appliedFilters.selectedProjectIds,
            region_ids: appliedFilters.selectedRegionIds,
            status: appliedFilters.statusFilter,
            lure_filter: appliedFilters.lureFilter,
            purchase_version: appliedFilters.purchaseVersionFilter || undefined,
            cloud_service_status: appliedFilters.cloudServiceStatusFilter,
            sort_by: sortBy,
            sort_order: sortOrder,
        }),
        [appliedFilters, pageSize, sortBy, sortOrder]
    )
    const previousDeviceQueryFiltersRef = useRef<DeviceQueryParams | null>(null)

    const loadDevices = useCallback(
        async (query: DeviceQueryParams) => {
            const requestId = latestDeviceRequestIdRef.current + 1
            latestDeviceRequestIdRef.current = requestId
            setLoading(true)
            try {
                const data = await queryDevices(query)
                if (requestId !== latestDeviceRequestIdRef.current) {
                    return
                }
                setDevices(data.items)
                setTotal(data.total)
            } catch {
                if (requestId === latestDeviceRequestIdRef.current) {
                    message.error(t('management.messages.loadFailed'))
                }
            } finally {
                if (requestId === latestDeviceRequestIdRef.current) {
                    setLoading(false)
                }
            }
        },
        [message, t]
    )

    const refreshDeviceList = useCallback(
        async (page = currentPage) => {
            await loadDevices({
                ...deviceQueryFilters,
                page,
            })
        },
        [currentPage, deviceQueryFilters, loadDevices]
    )

    const closeDeviceActionModals = useCallback(() => {
        environmentRefreshRequestIdRef.current += 1
        setEnvironmentRefreshing(false)
        closeEditModal()
        setViewModalVisible(false)
        setMapModalVisible(false)
        setCurrentDevice(null)
    }, [closeEditModal])

    const handleDeviceStale = useCallback(
        async (page = currentPage) => {
            closeDeviceActionModals()
            message.error(t('management.messages.deviceStale'))
            await refreshDeviceList(page)
        },
        [closeDeviceActionModals, currentPage, message, refreshDeviceList, t]
    )

    const refreshDeviceBeforeAction = useCallback(
        async (
            device: Device,
            onReady: (latestDevice: Device) => void,
            failureMessage: string
        ) => {
            try {
                const latestDevice = await getDevice(device.id)
                onReady(latestDevice)
            } catch (error) {
                if (isDeviceStaleError(error)) {
                    await handleDeviceStale()
                    return
                }
                message.error(failureMessage)
            }
        },
        [handleDeviceStale, message]
    )

    const refreshEnvironmentDetail = async () => {
        if (!currentDevice || environmentRefreshing) return
        const deviceId = currentDevice.id
        const requestId = ++environmentRefreshRequestIdRef.current
        setEnvironmentRefreshing(true)
        try {
            const latestDevice = await getDevice(deviceId)
            if (requestId !== environmentRefreshRequestIdRef.current) return
            setCurrentDevice((current) =>
                current?.id === deviceId
                    ? {
                          ...current,
                          latest_environment: latestDevice.latest_environment,
                      }
                    : current
            )
            setDevices((current) =>
                current.map((device) =>
                    device.id === deviceId
                        ? {
                              ...device,
                              latest_environment:
                                  latestDevice.latest_environment,
                          }
                        : device
                )
            )
        } catch (error) {
            if (requestId !== environmentRefreshRequestIdRef.current) return
            if (isDeviceStaleError(error)) {
                await handleDeviceStale()
            } else {
                message.error(t('environment.refreshFailed'))
            }
        } finally {
            if (requestId === environmentRefreshRequestIdRef.current) {
                setEnvironmentRefreshing(false)
            }
        }
    }

    const getInstallationImageUrl = useCallback(
        (path: string | null | undefined) =>
            path ? imageUrlMap.get(path) || path : null,
        [imageUrlMap]
    )

    useEffect(() => {
        void loadFilterOptions()
    }, [loadFilterOptions])

    useEffect(() => {
        const loadProjects = async () => {
            try {
                const projects = await lookupProjects({
                    includeDisabled: true,
                    forManagement: true,
                })
                setProjectOptions(projects)
            } catch {
                setProjectOptions([])
            }
        }

        void loadProjects()
    }, [])

    useEffect(() => {
        const hydrateImageUrls = async () => {
            try {
                const urlMap = await resolveManagedObjectUrls(
                    devices.map((device) => device.installation_image_path)
                )
                setImageUrlMap(urlMap)
            } catch {
                setImageUrlMap(new Map())
            }
        }

        void hydrateImageUrls()
    }, [devices])

    useEffect(() => {
        const nextFilters: DeviceManagementFilters = {
            ...createDefaultManagementFilters(),
            selectedProjectIds: routeProjectIds,
            statusFilter: routeStatusFilter,
            lureFilter: routeLureFilter,
        }
        setSearchName(nextFilters.searchName)
        setAppliedFilters(nextFilters)
        setSelectedRegionIds(nextFilters.selectedRegionIds)
        setStatusFilter(nextFilters.statusFilter)
        setLureFilter(nextFilters.lureFilter)
        setPurchaseVersionFilter(nextFilters.purchaseVersionFilter)
        setCloudServiceStatusFilter(nextFilters.cloudServiceStatusFilter)
        setCurrentPage(1)
    }, [routeLureFilter, routeProjectIds, routeStatusFilter])

    const formatDateTime = useCallback(
        (value: string | null | undefined) =>
            localeService.formatDate(value, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            }),
        []
    )

    const formatBusinessDate = useCallback(
        (value: string | null | undefined) =>
            formatPackageBusinessDate(value ?? null),
        []
    )

    const regionOptions = useMemo(
        () =>
            Object.entries(regionOptionLabels)
                .map(([value, label]) => ({
                    value: Number(value),
                    label,
                }))
                .sort((left, right) => left.label.localeCompare(right.label)),
        [regionOptionLabels]
    )
    const regionSelectOptions = useMemo(
        () =>
            buildAllFilterOptions(
                t('management.filters.allRegions'),
                regionOptions
            ),
        [regionOptions, t]
    )
    const projectSelectOptions = useMemo(
        () =>
            buildAllFilterOptions(
                t('management.filters.allProjects'),
                exportProjectOptions
                    .map((project) => ({
                        value: project.id,
                        label: project.label,
                    }))
                    .sort((left, right) =>
                        left.label.localeCompare(right.label)
                    )
            ),
        [exportProjectOptions, t]
    )
    const lureFilterOptions = useMemo(
        () => [
            {
                label: t('management.filters.lureOptions.all'),
                value: 'all' as DeviceLureFilter,
            },
            {
                label: t('management.filters.lureOptions.expiring1Days'),
                value: 'expiring_1_days' as DeviceLureFilter,
            },
            {
                label: t('management.filters.lureOptions.expiring3Days'),
                value: 'expiring_3_days' as DeviceLureFilter,
            },
            {
                label: t('management.filters.lureOptions.expiring7Days'),
                value: 'expiring_7_days' as DeviceLureFilter,
            },
            {
                label: t('management.filters.lureOptions.expiring15Days'),
                value: 'expiring_15_days' as DeviceLureFilter,
            },
            {
                label: t('management.filters.lureOptions.expired'),
                value: 'expired' as DeviceLureFilter,
            },
        ],
        [t]
    )
    const purchaseVersionFilterOptions = useMemo(
        () => [
            {
                label: t('management.filters.versionPlaceholder'),
                value: '',
            },
            {
                label: t('viewModal.notSet'),
                value: DEVICE_PURCHASE_VERSION_UNSET_FILTER,
            },
            ...purchaseVersionOptions.map((option) => ({
                label: getDevicePackageOptionDisplayName(option, t),
                value: option.value,
            })),
        ],
        [purchaseVersionOptions, t]
    )
    const draftFilters = useMemo<DeviceManagementFilters>(
        () => ({
            searchName,
            selectedProjectIds: appliedFilters.selectedProjectIds,
            selectedRegionIds,
            statusFilter,
            lureFilter,
            purchaseVersionFilter,
            cloudServiceStatusFilter,
        }),
        [
            appliedFilters.selectedProjectIds,
            cloudServiceStatusFilter,
            lureFilter,
            purchaseVersionFilter,
            searchName,
            selectedRegionIds,
            statusFilter,
        ]
    )
    const hasNonDefaultFilters = (filters: DeviceManagementFilters) =>
        filters.searchName.trim() !== '' ||
        filters.selectedProjectIds.length > 0 ||
        filters.selectedRegionIds.length > 0 ||
        filters.statusFilter !== 'all' ||
        filters.lureFilter !== 'all' ||
        filters.purchaseVersionFilter !== '' ||
        filters.cloudServiceStatusFilter !== 'all'
    const hasActiveFilters =
        hasNonDefaultFilters(draftFilters) ||
        hasNonDefaultFilters(appliedFilters)

    useEffect(() => {
        void queryTrigger

        const filtersChanged =
            previousDeviceQueryFiltersRef.current !== null &&
            previousDeviceQueryFiltersRef.current !== deviceQueryFilters

        if (filtersChanged && currentPage !== 1) {
            setCurrentPage(1)
            return
        }

        previousDeviceQueryFiltersRef.current = deviceQueryFilters
        void loadDevices({
            ...deviceQueryFilters,
            page: currentPage,
        })
    }, [currentPage, deviceQueryFilters, loadDevices, queryTrigger])

    useEffect(() => {
        const loadExportPreview = async () => {
            try {
                const data = await queryDevices({
                    page: 1,
                    page_size: 1,
                    project_ids: exportProjectIds,
                    region_ids: exportRegionIds,
                    status: exportStatusFilter,
                    lure_filter: exportLureFilter,
                    purchase_version: exportPurchaseVersionFilter || undefined,
                    cloud_service_status: exportCloudServiceStatusFilter,
                })
                setExportPreviewCount(data.total)
            } catch {
                setExportPreviewCount(0)
            }
        }

        if (!exportModalVisible) {
            return
        }

        void loadExportPreview()
    }, [
        exportLureFilter,
        exportModalVisible,
        exportPurchaseVersionFilter,
        exportCloudServiceStatusFilter,
        exportProjectIds,
        exportRegionIds,
        exportStatusFilter,
    ])

    const openEditModal = useCallback(
        (device: Device) => {
            editImageUploadRequestIdRef.current += 1
            activeEditDeviceIdRef.current = device.id
            setEditImageUploading(false)
            clearEditImageObjectUrl()
            setCurrentDevice(device)
            setEditImagePreviewUrl(
                getInstallationImageUrl(device.installation_image_path)
            )
            form.setFieldsValue({
                device_id: device.id,
                name: device.name,
                remark: device.remark,
                type: device.type,
                region_id: device.region_id,
                project_id: device.project_id,
                installation_image_path: device.installation_image_path,
            })
            setEditModalVisible(true)
        },
        [clearEditImageObjectUrl, form, getInstallationImageUrl]
    )

    const openViewModal = useCallback((device: Device) => {
        environmentRefreshRequestIdRef.current += 1
        setEnvironmentRefreshing(false)
        setCurrentDevice(device)
        setViewModalVisible(true)
    }, [])

    const openLocationModal = useCallback((device: Device) => {
        setCurrentDevice(device)
        setMapModalVisible(true)
    }, [])

    const handleEdit = (device: Device) => {
        void refreshDeviceBeforeAction(
            device,
            openEditModal,
            t('management.messages.updateFailed')
        )
    }

    const handleView = (device: Device) => {
        void refreshDeviceBeforeAction(
            device,
            openViewModal,
            t('management.messages.loadFailed')
        )
    }

    const handleSelectLocation = (device: Device) => {
        void refreshDeviceBeforeAction(
            device,
            openLocationModal,
            t('management.messages.locationUpdateFailed')
        )
    }

    const handleSubmitEdit = async () => {
        if (editImageUploading) {
            message.error(t('management.messages.imageUploadInProgress'))
            return
        }

        try {
            const values = await form.validateFields()
            const deviceUpdate: DeviceUpdate = {
                name: values.name,
                remark: values.remark ?? null,
                type: values.type,
                region_id: values.region_id,
                project_id: values.project_id,
                installation_image_path: values.installation_image_path || null,
            }

            if (currentDevice) {
                await updateDevice(currentDevice.id, deviceUpdate)
                invalidateManagedObjectUrlCache([
                    deviceUpdate.installation_image_path,
                ])
                message.success(t('management.messages.updateSuccess'))
                closeEditModal()
                await refreshDeviceList()
                try {
                    await loadFilterOptions()
                } catch {
                    message.error(t('management.messages.loadFailed'))
                }
            }
        } catch (error) {
            if (error && typeof error === 'object' && 'errorFields' in error) {
                return
            }
            if (isDeviceStaleError(error)) {
                await handleDeviceStale()
                return
            }
            message.error(
                getLocalizedApiErrorMessage(
                    error,
                    t('management.messages.updateFailed'),
                    t
                )
            )
        }
    }

    const handleDelete = async (deviceId: string) => {
        try {
            await deleteDevice(deviceId)
            message.success(t('management.messages.deleteSuccess'))
            const nextTotal = Math.max(total - 1, 0)
            const maxPage = Math.max(1, Math.ceil(nextTotal / pageSize))
            const nextPage = Math.min(currentPage, maxPage)
            setCurrentPage(nextPage)
            await refreshDeviceList(nextPage)
        } catch (error) {
            if (isDeviceStaleError(error)) {
                await handleDeviceStale()
                return
            }
            message.error(t('management.messages.deleteFailed'))
        }
    }

    const handleLocationSelected = async (lng: number, lat: number) => {
        if (currentDevice) {
            try {
                await updateDevice(currentDevice.id, { lng, lat })
                message.success(t('management.messages.locationUpdateSuccess'))
                setMapModalVisible(false)
                await refreshDeviceList()
            } catch (error) {
                if (isDeviceStaleError(error)) {
                    await handleDeviceStale()
                    return
                }
                message.error(t('management.messages.locationUpdateFailed'))
            }
        }
    }

    const openExportModal = () => {
        setExportProjectIds([])
        setExportRegionIds([...appliedFilters.selectedRegionIds])
        setExportStatusFilter(appliedFilters.statusFilter)
        setExportLureFilter(appliedFilters.lureFilter)
        setExportPurchaseVersionFilter(appliedFilters.purchaseVersionFilter)
        setExportCloudServiceStatusFilter(
            appliedFilters.cloudServiceStatusFilter
        )
        setExportModalVisible(true)
    }

    const handleInstallationImageUpload = async (file: File) => {
        if (!currentDevice) {
            return Upload.LIST_IGNORE
        }

        const deviceId = currentDevice.id
        const uploadRequestId = editImageUploadRequestIdRef.current + 1
        editImageUploadRequestIdRef.current = uploadRequestId
        activeEditDeviceIdRef.current = deviceId
        const previousImagePath =
            form.getFieldValue('installation_image_path') ??
            currentDevice.installation_image_path ??
            null
        const previousPreviewUrl =
            editImagePreviewUrl ?? getInstallationImageUrl(previousImagePath)

        const isCurrentUpload = () =>
            editImageUploadRequestIdRef.current === uploadRequestId &&
            activeEditDeviceIdRef.current === deviceId

        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            message.error(t('editModal.imageTypeInvalid'))
            return Upload.LIST_IGNORE
        }

        const maxSizeMb = 5
        if (file.size > maxSizeMb * 1024 * 1024) {
            message.error(t('editModal.imageSizeInvalid', { size: maxSizeMb }))
            return Upload.LIST_IGNORE
        }

        clearEditImageObjectUrl()
        const nextPreviewUrl = URL.createObjectURL(file)
        editImageObjectUrlRef.current = nextPreviewUrl
        setEditImagePreviewUrl(nextPreviewUrl)
        setEditImageUploading(true)

        try {
            const result = await uploadDeviceInstallationImage(deviceId, file)
            if (isCurrentUpload()) {
                form.setFieldValue('installation_image_path', result.object_key)
            }
        } catch {
            if (isCurrentUpload()) {
                clearEditImageObjectUrl()
                form.setFieldValue('installation_image_path', previousImagePath)
                setEditImagePreviewUrl(previousPreviewUrl)
                message.error(t('management.messages.imageUploadFailed'))
            }
        } finally {
            if (isCurrentUpload()) {
                setEditImageUploading(false)
            }
        }

        return Upload.LIST_IGNORE
    }

    const handleSearchFilters = () => {
        setAppliedFilters({
            ...draftFilters,
            searchName: normalizeSearchText(draftFilters.searchName),
            selectedRegionIds: [...draftFilters.selectedRegionIds],
        })
        setCurrentPage(1)
        setQueryTrigger((value) => value + 1)
    }

    const handleResetFilters = () => {
        if (!hasActiveFilters) {
            return
        }

        const defaultFilters = createDefaultManagementFilters()
        setSearchName(defaultFilters.searchName)
        setSelectedRegionIds(defaultFilters.selectedRegionIds)
        setStatusFilter(defaultFilters.statusFilter)
        setLureFilter(defaultFilters.lureFilter)
        setPurchaseVersionFilter(defaultFilters.purchaseVersionFilter)
        setCloudServiceStatusFilter(defaultFilters.cloudServiceStatusFilter)
        setAppliedFilters(defaultFilters)
        setCurrentPage(1)
    }

    const handleExport = async () => {
        if (exportPreviewCount === 0) {
            message.warning(t('management.messages.exportEmpty'))
            return
        }

        setExporting(true)
        try {
            await exportDevices({
                project_ids: exportProjectIds,
                region_ids: exportRegionIds,
                status: exportStatusFilter,
                lure_filter: exportLureFilter,
                purchase_version: exportPurchaseVersionFilter || undefined,
                cloud_service_status: exportCloudServiceStatusFilter,
            })
            setExportModalVisible(false)
            message.success(t('management.messages.exportSuccess'))
        } catch {
            message.error(t('management.messages.exportFailed'))
        } finally {
            setExporting(false)
        }
    }

    const renderCloudServiceStatus = (
        status?: Device['cloud_service_status']
    ) => {
        const normalizedStatus = status || 'unknown'
        const color =
            normalizedStatus === 'normal'
                ? 'green'
                : normalizedStatus === 'expiring'
                  ? 'orange'
                  : normalizedStatus === 'overdue'
                    ? 'red'
                    : 'default'
        return (
            <Tag color={color}>
                {t(`management.cloudServiceStatus.${normalizedStatus}`)}
            </Tag>
        )
    }

    const renderCurrentVersion = (
        version: string | null,
        packageKey?: string | null
    ) =>
        version ? (
            <Tag color="blue" data-testid="device-current-version-tag">
                {getDevicePackageDisplayName(packageKey, version, t)}
            </Tag>
        ) : (
            <Typography.Text type="secondary">
                {t('viewModal.notSet')}
            </Typography.Text>
        )

    const renderStatusWithTime = (
        tag: React.ReactNode,
        time: string | null | undefined,
        testId: string,
        formatTime: (
            value: string | null | undefined
        ) => string = formatDateTime
    ) => (
        <Space direction="vertical" size={2}>
            {tag}
            <Typography.Text type="secondary" data-testid={testId}>
                {formatTime(time)}
            </Typography.Text>
        </Space>
    )

    const renderCloudServiceStatusWithExpiry = (_: unknown, record: Device) => {
        return renderStatusWithTime(
            renderCloudServiceStatus(record.cloud_service_status),
            record.cloud_service_expires_at,
            `device-cloud-service-expires-at-${record.id}`,
            formatBusinessDate
        )
    }

    const getStatusChangedAt = (record: Device) =>
        record.status_changed_at || record.last_active_at || record.created_at

    const renderDeviceStatusWithChangedAt = (_: unknown, record: Device) => {
        const color = record.status === 1 ? 'green' : 'red'
        const text =
            record.status === 1
                ? t('management.deviceStatus.online')
                : t('management.deviceStatus.offline')

        return renderStatusWithTime(
            <Tag color={color}>{text}</Tag>,
            getStatusChangedAt(record),
            `device-status-changed-at-${record.id}`
        )
    }

    const formatEnvironmentValue = (
        value: number | null,
        fractionDigits: number
    ) =>
        value === null || !Number.isFinite(value)
            ? '--'
            : value.toFixed(fractionDigits)

    const renderEnvironmentMetric = (
        key: 'temperature' | 'humidity' | 'co2',
        value: number | null,
        unit: string,
        fractionDigits: number,
        testId?: string
    ) => (
        <div className="device-environment-metric">
            <span className="device-environment-metric-label">
                {t(`environment.metrics.${key}`)}
            </span>
            <span className="device-environment-metric-reading">
                <strong data-testid={testId}>
                    {formatEnvironmentValue(value, fractionDigits)}
                </strong>
                {value !== null && (
                    <span className="device-environment-metric-unit">
                        {unit}
                    </span>
                )}
            </span>
        </div>
    )

    const renderEnvironmentSummary = (device: Device) => {
        const sample = device.latest_environment
        if (!sample) {
            return (
                <Typography.Text type="secondary">
                    {t('environment.noData')}
                </Typography.Text>
            )
        }

        return (
            <div
                className="device-environment-cell"
                data-testid={`device-environment-${device.id}`}
            >
                <div className="device-environment-metrics">
                    {renderEnvironmentMetric(
                        'temperature',
                        sample.temperature_c,
                        '°C',
                        1,
                        `device-temperature-${device.id}`
                    )}
                    {renderEnvironmentMetric(
                        'humidity',
                        sample.humidity_percent,
                        '%RH',
                        1,
                        `device-humidity-${device.id}`
                    )}
                    {renderEnvironmentMetric(
                        'co2',
                        sample.co2_ppm,
                        'ppm',
                        0,
                        `device-co2-${device.id}`
                    )}
                </div>
            </div>
        )
    }

    const getDeviceTypeText = (type: number | null) => {
        if (type === 1) {
            return t('management.deviceTypes.mosquitoKiller')
        }

        return type === null
            ? t('viewModal.notSet')
            : t('management.deviceTypes.other')
    }

    const getRegionLabelForDevice = (device: Device) =>
        device.region_id
            ? regionOptionLabels[device.region_id] || String(device.region_id)
            : t('viewModal.notSet')

    const viewModalBodyStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    }
    const viewCardStyle: React.CSSProperties = {
        borderRadius: 6,
    }
    const viewImageBoxStyle: React.CSSProperties = {
        height: 244,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
    }
    const viewImageStyle: React.CSSProperties = {
        maxWidth: '100%',
        maxHeight: 224,
        objectFit: 'contain',
    }
    const viewDetailsGridStyle: React.CSSProperties = {
        display: 'grid',
        gap: 16,
    }
    const viewDetailListStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    }
    const viewRuntimeGridStyle: React.CSSProperties = {
        display: 'grid',
        columnGap: 48,
        rowGap: 12,
    }
    const viewDetailFieldStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        minHeight: 20,
        lineHeight: '20px',
    }
    const viewDetailLabelStyle: React.CSSProperties = {
        color: '#8c8c8c',
        marginRight: 12,
        flex: '0 0 auto',
    }
    const viewDetailValueStyle: React.CSSProperties = {
        color: 'rgba(0, 0, 0, 0.88)',
        wordBreak: 'break-word',
        minWidth: 0,
    }

    const renderDetailField = (
        label: React.ReactNode,
        value: React.ReactNode
    ) => (
        <div style={viewDetailFieldStyle}>
            <span style={viewDetailLabelStyle}>{label}</span>
            <span style={viewDetailValueStyle}>{value}</span>
        </div>
    )

    const renderViewModalContent = (device: Device) => {
        const imageUrl = getInstallationImageUrl(device.installation_image_path)
        const runtimeStatus =
            device.status === 1
                ? t('management.deviceStatus.online')
                : t('management.deviceStatus.offline')

        return (
            <div style={viewModalBodyStyle}>
                <Card
                    size="small"
                    title={t('management.columns.deviceImage')}
                    style={viewCardStyle}
                >
                    <div style={viewImageBoxStyle}>
                        {imageUrl ? (
                            <Image
                                src={imageUrl}
                                preview={{ src: imageUrl }}
                                alt={t('management.columns.deviceImage')}
                                style={viewImageStyle}
                            />
                        ) : (
                            <Typography.Text type="secondary">
                                <PictureOutlined />{' '}
                                {t('viewModal.noDeviceImage')}
                            </Typography.Text>
                        )}
                    </div>
                </Card>

                <div
                    className="device-view-details-grid"
                    style={viewDetailsGridStyle}
                >
                    <Card
                        size="small"
                        title={t('viewModal.basicInfo')}
                        style={viewCardStyle}
                    >
                        <div style={viewDetailListStyle}>
                            {renderDetailField(
                                t('management.columns.name'),
                                device.name || t('management.noAddress')
                            )}
                            {renderDetailField(
                                t('management.columns.deviceId'),
                                device.id
                            )}
                            {renderDetailField(
                                t('management.columns.projectName'),
                                getDeviceProjectDisplayName(device)
                            )}
                            {renderDetailField(
                                t('management.columns.region'),
                                getRegionLabelForDevice(device)
                            )}
                            {renderDetailField(
                                t('management.columns.type'),
                                getDeviceTypeText(device.type)
                            )}
                            {renderDetailField(
                                t('management.columns.remark'),
                                device.remark || t('viewModal.notSet')
                            )}
                            {renderDetailField(
                                t('management.columns.createdAt'),
                                formatDateTime(device.created_at)
                            )}
                        </div>
                    </Card>

                    <Card
                        size="small"
                        title={t('viewModal.serviceInfo')}
                        style={viewCardStyle}
                    >
                        <div style={viewDetailListStyle}>
                            {renderDetailField(
                                t('management.columns.currentVersion'),
                                device.purchase_version
                                    ? getDevicePackageDisplayName(
                                          device.package_key,
                                          device.purchase_version,
                                          t
                                      )
                                    : t('viewModal.notSet')
                            )}
                            {renderDetailField(
                                t('management.columns.cloudServiceStatus'),
                                renderCloudServiceStatus(
                                    device.cloud_service_status
                                )
                            )}
                            {renderDetailField(
                                t('viewModal.cloudServiceExpiresAt'),
                                formatBusinessDate(
                                    device.cloud_service_expires_at
                                )
                            )}
                            {renderDetailField(
                                t('management.columns.lureRemindAt'),
                                formatBusinessDate(device.lure_remind_at)
                            )}
                        </div>
                    </Card>
                </div>

                <Card
                    size="small"
                    title={t('viewModal.runtimeInfo')}
                    style={viewCardStyle}
                >
                    <div
                        className="device-view-details-grid"
                        style={viewRuntimeGridStyle}
                    >
                        {renderDetailField(
                            t('management.columns.status'),
                            <Tag color={device.status === 1 ? 'green' : 'red'}>
                                {runtimeStatus}
                            </Tag>
                        )}
                        {renderDetailField(
                            t('management.columns.lastImageUploadedAt'),
                            formatDateTime(device.last_image_uploaded_at)
                        )}
                        {renderDetailField(
                            t('viewModal.location'),
                            `${device.lng ?? '-'}, ${device.lat ?? '-'}`
                        )}
                    </div>
                </Card>

                <Card
                    size="small"
                    title={t('environment.title')}
                    extra={
                        <Space size={8}>
                            <Tooltip title={t('environment.refreshDetail')}>
                                <Button
                                    type="text"
                                    size="small"
                                    aria-label={t('environment.refreshDetail')}
                                    icon={<ReloadOutlined />}
                                    style={{ width: 24, minWidth: 24 }}
                                    loading={environmentRefreshing}
                                    disabled={environmentRefreshing}
                                    onClick={() =>
                                        void refreshEnvironmentDetail()
                                    }
                                />
                            </Tooltip>
                        </Space>
                    }
                    style={viewCardStyle}
                >
                    {device.latest_environment ? (
                        <div
                            className="device-environment-detail"
                            data-testid="device-environment-detail"
                        >
                            <div className="device-environment-detail-metrics">
                                {renderEnvironmentMetric(
                                    'temperature',
                                    device.latest_environment.temperature_c,
                                    '°C',
                                    1
                                )}
                                {renderEnvironmentMetric(
                                    'humidity',
                                    device.latest_environment.humidity_percent,
                                    '%RH',
                                    1
                                )}
                                {renderEnvironmentMetric(
                                    'co2',
                                    device.latest_environment.co2_ppm,
                                    'ppm',
                                    0
                                )}
                            </div>
                            <div className="device-environment-detail-meta">
                                {renderDetailField(
                                    t('environment.sampledAt'),
                                    formatDateTime(
                                        device.latest_environment.sampled_at
                                    )
                                )}
                                {device.latest_environment.warnings.length >
                                    0 &&
                                    renderDetailField(
                                        t('environment.warning'),
                                        device.latest_environment.warnings
                                            .map((warning) =>
                                                t(
                                                    `environment.warnings.${warning}`,
                                                    {
                                                        defaultValue: warning,
                                                    }
                                                )
                                            )
                                            .join('、')
                                    )}
                                {device.latest_environment.error &&
                                    renderDetailField(
                                        t('environment.error'),
                                        device.latest_environment.error
                                    )}
                            </div>
                        </div>
                    ) : (
                        <Typography.Text type="secondary">
                            {t('environment.noData')}
                        </Typography.Text>
                    )}
                </Card>
            </div>
        )
    }

    const actionColumnStyle: React.CSSProperties = {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: 8,
        maxWidth: '100%',
    }
    const actionLinkButtonStyle: React.CSSProperties = {
        paddingInline: 4,
        height: 24,
        lineHeight: '24px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    }
    const renderProjectName = (device: Device) => {
        const projectName = getDeviceProjectDisplayName(device)
        const rootProjectName = getDeviceRootProjectDisplayName(device)
        const shouldShowRootProject =
            rootProjectName.length > 0 && rootProjectName !== projectName
        const rootProjectDisplayText = `总项目: ${rootProjectName}`

        return (
            <div className="device-project-name-cell">
                {renderPaginatedListText(projectName)}
                {shouldShowRootProject && (
                    <Typography.Text
                        className="device-project-name-root"
                        data-testid={`device-root-project-name-${device.id}`}
                        title={rootProjectDisplayText}
                        type="secondary"
                    >
                        {rootProjectDisplayText}
                    </Typography.Text>
                )}
            </div>
        )
    }

    const columns: TableColumnsType<Device> = [
        {
            title: t('management.columns.address'),
            dataIndex: 'name',
            key: 'name',
            width: 180,
            ellipsis: true,
            render: (text: string | null) =>
                renderPaginatedListText(text || t('management.noAddress')),
        },
        {
            title: t('management.columns.projectName'),
            dataIndex: 'project_name',
            key: 'project_name',
            width: 160,
            render: (_: unknown, record: Device) => renderProjectName(record),
        },
        {
            title: t('management.columns.deviceImage'),
            dataIndex: 'installation_image_path',
            key: 'installation_image_path',
            width: 104,
            render: (path: string | null) => {
                const imageUrl = getInstallationImageUrl(path)
                return imageUrl ? (
                    <Image
                        src={imageUrl}
                        preview={{ src: imageUrl }}
                        alt={t('management.columns.deviceImage')}
                        width={48}
                        height={48}
                        style={{ objectFit: 'cover' }}
                    />
                ) : (
                    <Typography.Text type="secondary">
                        {t('editModal.noInstallationImage')}
                    </Typography.Text>
                )
            },
        },
        {
            title: t('management.columns.currentVersion'),
            dataIndex: 'purchase_version',
            key: 'purchase_version',
            width: i18n.resolvedLanguage === 'en' ? 240 : 112,
            render: (value: string | null, record: Device) =>
                renderCurrentVersion(value, record.package_key),
        },
        {
            title: t('management.columns.lureRemindAt'),
            dataIndex: 'lure_remind_at',
            key: 'lure_remind_at',
            width: 128,
            sorter: true,
            render: (text: string | null, record: Device) => (
                <span data-testid={`device-lure-remind-at-${record.id}`}>
                    {formatBusinessDate(text)}
                </span>
            ),
        },
        {
            title: t('management.columns.cloudServiceStatus'),
            dataIndex: 'cloud_service_status',
            key: 'cloud_service_status',
            width: 132,
            render: renderCloudServiceStatusWithExpiry,
        },
        {
            title: t('management.columns.environment'),
            dataIndex: 'latest_environment',
            key: 'latest_environment',
            width: 300,
            render: (_: unknown, record: Device) =>
                renderEnvironmentSummary(record),
        },
        {
            title: t('management.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 168,
            render: renderDeviceStatusWithChangedAt,
            sorter: true,
        },
        {
            title: t('management.columns.actions'),
            key: 'action',
            width: i18n.resolvedLanguage === 'en' ? 360 : 316,
            fixed: 'right',
            render: (_: unknown, record: Device) => (
                <div data-testid="device-action-wrap" style={actionColumnStyle}>
                    {canReadDeviceDetails && (
                        <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => handleView(record)}
                            style={actionLinkButtonStyle}
                        >
                            {t('management.actions.viewDetails')}
                        </Button>
                    )}
                    {canUpdateDevices && (
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            style={actionLinkButtonStyle}
                        >
                            {t('management.actions.edit')}
                        </Button>
                    )}
                    {canUpdateDeviceLocation && (
                        <Button
                            type="link"
                            icon={<EnvironmentOutlined />}
                            onClick={() => handleSelectLocation(record)}
                            style={actionLinkButtonStyle}
                        >
                            {t('management.actions.location')}
                        </Button>
                    )}
                    {canDeleteDevices && (
                        <Popconfirm
                            title={t('management.confirmDelete')}
                            open={openDeleteDeviceId === record.id}
                            onOpenChange={(open) => {
                                if (open) {
                                    setOpenDeleteDeviceId(record.id)
                                }
                            }}
                            onCancel={() => setOpenDeleteDeviceId(null)}
                            onConfirm={() => {
                                setOpenDeleteDeviceId(null)
                                handleDelete(record.id)
                            }}
                            okText={t('common:buttons.ok')}
                            cancelText={t('common:buttons.cancel')}
                        >
                            <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                style={actionLinkButtonStyle}
                            >
                                {t('management.actions.delete')}
                            </Button>
                        </Popconfirm>
                    )}
                </div>
            ),
        },
    ]
    const tableScrollWidth = columns.reduce(
        (total, column) => total + Number(column.width),
        0
    )
    return (
        <PageLayout
            title={t('management.title')}
            breadcrumb={[
                { title: t('navigation:breadcrumb.deviceManagement') },
            ]}
        >
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <FilterBar data-testid="device-filter-panel">
                    <FilterField label={t('management.columns.address')}>
                        <Select
                            aria-label={t('management.searchPlaceholder')}
                            placeholder={t('management.searchPlaceholder')}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            filterOption={filterSearchOptionByLabel}
                            value={searchName || undefined}
                            onChange={(value) => setSearchName(value || '')}
                            options={nameOptions.map((name) => ({
                                label: name,
                                value: name,
                            }))}
                        />
                    </FilterField>

                    <FilterField label={t('management.columns.status')}>
                        <Select<DeviceStatusFilter>
                            aria-label={t('management.columns.status')}
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={[
                                {
                                    label: t(
                                        'management.filters.statusOptions.all'
                                    ),
                                    value: 'all',
                                },
                                {
                                    label: t('management.deviceStatus.online'),
                                    value: 'online',
                                },
                                {
                                    label: t('management.deviceStatus.offline'),
                                    value: 'offline',
                                },
                            ]}
                        />
                    </FilterField>

                    <FilterField label={t('management.columns.currentVersion')}>
                        <Select
                            aria-label={t('management.columns.currentVersion')}
                            showSearch
                            optionFilterProp="label"
                            filterOption={filterSearchOptionByLabel}
                            placeholder={t(
                                'management.filters.versionPlaceholder'
                            )}
                            value={purchaseVersionFilter}
                            options={purchaseVersionFilterOptions}
                            onChange={(value) =>
                                setPurchaseVersionFilter(value || '')
                            }
                        />
                    </FilterField>

                    <FilterField
                        label={t('management.columns.cloudServiceStatus')}
                    >
                        <Select<
                            NonNullable<Device['cloud_service_status']> | 'all'
                        >
                            aria-label={t(
                                'management.columns.cloudServiceStatus'
                            )}
                            value={cloudServiceStatusFilter}
                            onChange={setCloudServiceStatusFilter}
                            options={[
                                {
                                    label: t(
                                        'management.filters.cloudServiceStatusOptions.all'
                                    ),
                                    value: 'all',
                                },
                                ...(
                                    [
                                        'unknown',
                                        'normal',
                                        'expiring',
                                        'overdue',
                                    ] as NonNullable<
                                        Device['cloud_service_status']
                                    >[]
                                ).map((status) => ({
                                    label: t(
                                        `management.cloudServiceStatus.${status}`
                                    ),
                                    value: status,
                                })),
                            ]}
                        />
                    </FilterField>

                    <FilterField label={t('management.columns.lureRemindAt')}>
                        <Select<DeviceLureFilter>
                            aria-label={t('management.columns.lureRemindAt')}
                            value={lureFilter}
                            onChange={setLureFilter}
                            options={lureFilterOptions}
                        />
                    </FilterField>

                    <FilterActions testId="device-filter-actions">
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearchFilters}
                            loading={loading}
                        >
                            {t('common:buttons.search')}
                        </Button>
                        <Button onClick={handleResetFilters}>
                            {t('management.filters.reset')}
                        </Button>
                    </FilterActions>
                </FilterBar>

                <PaginatedListContainer>
                    <PaginatedListToolbar data-testid="device-list-toolbar">
                        {canExportDevices ? (
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={openExportModal}
                                loading={exporting}
                                disabled={loading}
                            >
                                {t('management.export.button')}
                            </Button>
                        ) : null}
                    </PaginatedListToolbar>

                    <PaginatedListCard>
                        <Table
                            className="device-management-table"
                            columns={columns}
                            dataSource={devices}
                            rowKey="id"
                            loading={loading}
                            size={PAGINATED_LIST_TABLE_SIZE}
                            scroll={{ x: tableScrollWidth }}
                            onChange={(_, __, sorter, extra) => {
                                if (extra.action !== 'sort') {
                                    return
                                }

                                if (Array.isArray(sorter)) {
                                    return
                                }

                                const field = sorter.field
                                const sortField =
                                    field === 'status'
                                        ? 'status_changed_at'
                                        : field
                                if (
                                    sortField === 'status_changed_at' ||
                                    sortField === 'last_active_at' ||
                                    sortField === 'lure_remind_at' ||
                                    sortField === 'created_at'
                                ) {
                                    setSortBy(sortField)
                                    setSortOrder(
                                        sorter.order === 'ascend' ||
                                            sorter.order === 'descend'
                                            ? sorter.order
                                            : undefined
                                    )
                                    setCurrentPage(1)
                                    return
                                }

                                setSortBy(undefined)
                                setSortOrder(undefined)
                                setCurrentPage(1)
                            }}
                            pagination={buildPaginatedListPagination({
                                current: currentPage,
                                pageSize,
                                total,
                                showTotal: (total) =>
                                    t('management.totalDevices', { total }),
                                onChange: (page, nextPageSize) => {
                                    const resolvedPageSize =
                                        nextPageSize ?? pageSize
                                    setPageSize(resolvedPageSize)
                                    setCurrentPage(
                                        resolvedPageSize !== pageSize ? 1 : page
                                    )
                                },
                            })}
                        />
                    </PaginatedListCard>
                </PaginatedListContainer>
            </Space>

            <Modal
                title={t('management.export.modalTitle')}
                open={exportModalVisible}
                onCancel={() => setExportModalVisible(false)}
                onOk={() => void handleExport()}
                okText={t('management.export.confirm')}
                cancelText={t('management.export.cancel')}
                confirmLoading={exporting}
                width={960}
                maskClosable={false}
                destroyOnClose={false}
            >
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                        <div style={{ marginBottom: 8 }}>
                            {t('management.columns.projectName')}
                        </div>
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            style={{ width: '100%' }}
                            value={getAllAwareDisplayValues(exportProjectIds)}
                            options={projectSelectOptions}
                            placeholder={t('management.filters.allProjects')}
                            optionFilterProp="label"
                            filterOption={filterSearchOptionByLabel}
                            maxTagCount="responsive"
                            onChange={(value) =>
                                setExportProjectIds(
                                    normalizeAllAwareValues(
                                        value as FilterSelectValue<number>[],
                                        exportProjectIds
                                    )
                                )
                            }
                        />
                    </Col>

                    <Col xs={24} md={12}>
                        <div style={{ marginBottom: 8 }}>
                            {t('management.columns.region')}
                        </div>
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            style={{ width: '100%' }}
                            value={getAllAwareDisplayValues(exportRegionIds)}
                            options={regionSelectOptions}
                            placeholder={t(
                                'management.filters.regionPlaceholder'
                            )}
                            optionFilterProp="label"
                            filterOption={filterRegionSelectOption}
                            maxTagCount="responsive"
                            onChange={(value) =>
                                setExportRegionIds(
                                    normalizeAllAwareValues(
                                        value as FilterSelectValue<number>[],
                                        exportRegionIds
                                    )
                                )
                            }
                        />
                    </Col>

                    <Col xs={24} md={12}>
                        <div style={{ marginBottom: 8 }}>
                            {t('management.columns.status')}
                        </div>
                        <Select<DeviceStatusFilter>
                            style={{ width: '100%' }}
                            value={exportStatusFilter}
                            onChange={setExportStatusFilter}
                            options={[
                                {
                                    label: t(
                                        'management.filters.statusOptions.all'
                                    ),
                                    value: 'all',
                                },
                                {
                                    label: t('management.deviceStatus.online'),
                                    value: 'online',
                                },
                                {
                                    label: t('management.deviceStatus.offline'),
                                    value: 'offline',
                                },
                            ]}
                        />
                    </Col>

                    <Col xs={24} md={12}>
                        <div style={{ marginBottom: 8 }}>
                            {t('management.columns.lureRemindAt')}
                        </div>
                        <Select<DeviceLureFilter>
                            style={{ width: '100%' }}
                            value={exportLureFilter}
                            onChange={setExportLureFilter}
                            options={lureFilterOptions}
                        />
                    </Col>

                    <Col xs={24} md={12}>
                        <div style={{ marginBottom: 8 }}>
                            {t('management.columns.currentVersion')}
                        </div>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            filterOption={filterSearchOptionByLabel}
                            style={{ width: '100%' }}
                            value={exportPurchaseVersionFilter}
                            placeholder={t(
                                'management.filters.versionPlaceholder'
                            )}
                            options={purchaseVersionFilterOptions}
                            onChange={(value) =>
                                setExportPurchaseVersionFilter(value || '')
                            }
                        />
                    </Col>

                    <Col xs={24} md={12}>
                        <div style={{ marginBottom: 8 }}>
                            {t('management.columns.cloudServiceStatus')}
                        </div>
                        <Select<
                            NonNullable<Device['cloud_service_status']> | 'all'
                        >
                            style={{ width: '100%' }}
                            value={exportCloudServiceStatusFilter}
                            onChange={setExportCloudServiceStatusFilter}
                            options={[
                                {
                                    label: t(
                                        'management.filters.cloudServiceStatusOptions.all'
                                    ),
                                    value: 'all',
                                },
                                ...(
                                    [
                                        'unknown',
                                        'normal',
                                        'expiring',
                                        'overdue',
                                    ] as NonNullable<
                                        Device['cloud_service_status']
                                    >[]
                                ).map((status) => ({
                                    label: t(
                                        `management.cloudServiceStatus.${status}`
                                    ),
                                    value: status,
                                })),
                            ]}
                        />
                    </Col>

                    <Col xs={24}>
                        <Typography.Text type="secondary">
                            {t('management.export.previewCount', {
                                count: exportPreviewCount,
                            })}
                        </Typography.Text>
                    </Col>
                </Row>
            </Modal>

            <Modal
                title={t('editModal.title')}
                open={editModalVisible}
                onOk={handleSubmitEdit}
                onCancel={closeEditModal}
                okText={t('common:buttons.save')}
                cancelText={t('common:buttons.cancel')}
                width="min(820px, calc(100vw - 32px))"
                className="device-edit-modal"
                wrapClassName="device-edit-modal-wrap"
                maskClosable={false}
                destroyOnClose
                confirmLoading={editImageUploading}
                okButtonProps={{ disabled: editImageUploading }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    className="device-edit-form"
                >
                    <div className="device-edit-layout">
                        <section
                            className="device-edit-image-panel"
                            role="group"
                            aria-label={t('editModal.installationImage')}
                        >
                            <Form.Item
                                label={t('editModal.installationImage')}
                                name="installation_image_path"
                                className="device-edit-image-item"
                                help={t('editModal.installationImageHelp', {
                                    size: 5,
                                })}
                            >
                                <Input type="hidden" />
                            </Form.Item>

                            <div className="device-edit-image-preview">
                                {editImagePreviewUrl ? (
                                    <Image
                                        src={editImagePreviewUrl}
                                        preview={{ src: editImagePreviewUrl }}
                                        alt={t('editModal.installationImage')}
                                        className="device-edit-image-preview-img"
                                    />
                                ) : null}
                            </div>

                            <div className="device-edit-image-actions">
                                <Upload
                                    showUploadList={false}
                                    beforeUpload={handleInstallationImageUpload}
                                    accept="image/jpeg,image/png,image/webp"
                                    disabled={editImageUploading}
                                >
                                    <Button
                                        icon={<UploadOutlined />}
                                        loading={editImageUploading}
                                        disabled={editImageUploading}
                                    >
                                        {t('editModal.uploadInstallationImage')}
                                    </Button>
                                </Upload>
                            </div>

                            {editImageUploading ? (
                                <Typography.Text
                                    type="secondary"
                                    className="device-edit-image-uploading"
                                >
                                    {t('editModal.installationImageUploading')}
                                </Typography.Text>
                            ) : null}
                        </section>

                        <section
                            className="device-edit-fields-panel"
                            role="group"
                            aria-label={t('editModal.title')}
                        >
                            <Form.Item
                                label={t('editModal.deviceId')}
                                name="device_id"
                            >
                                <Input disabled />
                            </Form.Item>

                            <Form.Item
                                label={t('editModal.deviceType')}
                                name="type"
                                required
                                rules={[
                                    {
                                        required: true,
                                        message: t(
                                            'editModal.deviceTypeRequired'
                                        ),
                                    },
                                ]}
                            >
                                <Select
                                    placeholder={t(
                                        'editModal.deviceTypePlaceholder'
                                    )}
                                >
                                    <Select.Option value={1}>
                                        {t(
                                            'management.deviceTypes.mosquitoDevice'
                                        )}
                                    </Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item
                                label={t('editModal.name')}
                                name="name"
                                required
                                rules={[
                                    {
                                        max: DEVICE_NAME_MAX_LENGTH,
                                        message: t('editModal.nameMaxLength', {
                                            max: DEVICE_NAME_MAX_LENGTH,
                                        }),
                                    },
                                    {
                                        validator: async (
                                            _,
                                            value: string | undefined
                                        ) => {
                                            if (
                                                typeof value === 'string' &&
                                                value.trim()
                                            ) {
                                                return
                                            }
                                            throw new Error(
                                                t('editModal.nameRequired')
                                            )
                                        },
                                    },
                                ]}
                            >
                                <Input
                                    placeholder={t('editModal.namePlaceholder')}
                                />
                            </Form.Item>
                            <Form.Item
                                label={t('editModal.region')}
                                name="region_id"
                                help={t('editModal.regionHelp')}
                                rules={[
                                    {
                                        required: true,
                                        message: t('editModal.regionRequired'),
                                    },
                                ]}
                            >
                                <RegionSelect allowClear={false} />
                            </Form.Item>
                            <Form.Item
                                label={t('editModal.project')}
                                name="project_id"
                                help={t('editModal.projectHelp')}
                                rules={[
                                    {
                                        required: true,
                                        message: t('editModal.projectRequired'),
                                    },
                                ]}
                            >
                                <Select
                                    allowClear
                                    showSearch
                                    placeholder={t(
                                        'editModal.projectPlaceholder'
                                    )}
                                    optionFilterProp="label"
                                    filterOption={filterSearchOptionByLabel}
                                    options={projectOptions.map((project) => ({
                                        label: project.name,
                                        value: project.id,
                                    }))}
                                />
                            </Form.Item>
                            <Form.Item
                                label={t('editModal.remark')}
                                name="remark"
                                rules={[
                                    {
                                        max: DEVICE_REMARK_MAX_LENGTH,
                                        message: t(
                                            'editModal.remarkMaxLength',
                                            {
                                                max: DEVICE_REMARK_MAX_LENGTH,
                                            }
                                        ),
                                    },
                                ]}
                            >
                                <Input.TextArea
                                    rows={3}
                                    placeholder={t(
                                        'editModal.remarkPlaceholder'
                                    )}
                                />
                            </Form.Item>
                        </section>
                    </div>
                </Form>
            </Modal>

            <Modal
                title={`${currentDevice?.name || currentDevice?.address || ''} ${t('viewModal.title')}`}
                open={viewModalVisible}
                onCancel={() => {
                    environmentRefreshRequestIdRef.current += 1
                    setEnvironmentRefreshing(false)
                    setViewModalVisible(false)
                    setCurrentDevice(null)
                }}
                footer={null}
                width={960}
                maskClosable={false}
            >
                {currentDevice && renderViewModalContent(currentDevice)}
            </Modal>

            <Modal
                title={t('mapModal.title')}
                open={mapModalVisible}
                onCancel={() => {
                    setMapModalVisible(false)
                    setCurrentDevice(null)
                }}
                footer={null}
                width={800}
                maskClosable={false}
                destroyOnClose
            >
                {currentDevice && (
                    <MapSelector
                        key={currentDevice.id}
                        currentLng={currentDevice.lng}
                        currentLat={currentDevice.lat}
                        onLocationSelected={handleLocationSelected}
                    />
                )}
            </Modal>
        </PageLayout>
    )
}

export default DeviceManagement
